import React, { useState, useEffect, useRef } from "react";
import { InputNumber, Space, message } from "antd";
import PropTypes from "prop-types";

export const normalizeBlockTimeMinutes = (value) => {
    if (value === undefined || value === null) {
        return null;
    }

    const totalMinutes = Number(value);
    if (!Number.isFinite(totalMinutes) || totalMinutes < 0 || !Number.isInteger(totalMinutes)) {
        return totalMinutes;
    }

    const onesDigit = totalMinutes % 10;
    if (onesDigit <= 2) {
        return totalMinutes - onesDigit;
    }
    if (onesDigit <= 6) {
        return totalMinutes + (5 - onesDigit);
    }
    return totalMinutes + (10 - onesDigit);
};

/**
 * 小时分钟输入组件
 * 允许用户分别输入小时和分钟，内部会自动转换为总分钟数
 * 特点：
 * 1. 分钟以5为倍数
 * 2. 允许输入超过60的分钟，会自动转换为小时+分钟
 * 3. 小时没有最大值
 * 4. 支持鼠标滚轮控制
 */
const HourMinuteInput = ({ value, onChange, disabled, style }) => {
    // 初始化小时和分钟
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);
    // 额外的分钟输入，用于处理可能超过60的分钟输入
    const [rawMinutes, setRawMinutes] = useState(0);
    const [hoursAnimating, setHoursAnimating] = useState(false);
    // 是否正在编辑分钟(用于跟踪编辑状态)
    const [isEditingMinutes, setIsEditingMinutes] = useState(false);
    const previousHoursRef = useRef(hours);

    // 当外部value变化时，更新小时和分钟
    useEffect(() => {
        // 如果正在编辑分钟，不响应外部value变化
        if (isEditingMinutes) {
            return;
        }

        if (value === undefined || value === null) {
            setHours(0);
            setMinutes(0);
            setRawMinutes(0);
            return;
        }

        const totalMinutes = Number(value);
        const normalizedTotalMinutes = normalizeBlockTimeMinutes(totalMinutes);
        const calculatedHours = Math.floor(normalizedTotalMinutes / 60);
        const calculatedMinutes = normalizedTotalMinutes % 60;

        setHours(calculatedHours);
        setMinutes(calculatedMinutes);
        setRawMinutes(calculatedMinutes);
        previousHoursRef.current = calculatedHours;

        if (normalizedTotalMinutes !== totalMinutes) {
            onChange && onChange(normalizedTotalMinutes);
        }
    }, [value, isEditingMinutes, onChange]);

    // 小时变化时，更新总分钟数
    const handleHoursChange = (newHours) => {
        if (newHours === null || newHours === undefined) newHours = 0;
        const newHoursNum = Number(newHours);

        setHours(newHoursNum);
        previousHoursRef.current = newHoursNum;

        // 计算并通知外部总分钟数变化
        const totalMinutes = newHoursNum * 60 + minutes;
        onChange && onChange(totalMinutes);
    };

    // 分钟输入开始处理
    const handleMinutesFocus = () => {
        setIsEditingMinutes(true);
    };

    // 分钟输入过程中的处理
    const handleMinutesChange = (newMinutes) => {
        if (newMinutes === null || newMinutes === undefined) newMinutes = 0;
        setRawMinutes(newMinutes);
        setIsEditingMinutes(true);
    };

    // 处理分钟输入框失去焦点事件，确保显示的是规范化后的值并执行自动计算
    const handleMinutesBlur = () => {
        // 输入完成，结束编辑状态
        setIsEditingMinutes(false);

        // 输入完成后，按 FlyMeta BH 分段规则归整为5的倍数。
        const roundedMinutes = normalizeBlockTimeMinutes(rawMinutes);

        // 处理可能超过60的分钟值
        const additionalHours = Math.floor(roundedMinutes / 60);
        const normalizedMinutes = roundedMinutes % 60;

        // 更新最终显示值
        const newHours = hours + additionalHours;

        // 如果小时发生变化，显示提示并播放动画
        if (additionalHours > 0) {
            message.info(`分钟超过60，已自动增加 ${additionalHours}小时`);
            setHoursAnimating(true);
            setTimeout(() => {
                setHoursAnimating(false);
            }, 1500); // 延长动画时间为1.5秒，让效果更明显
        }

        setHours(newHours);
        setMinutes(normalizedMinutes);
        setRawMinutes(normalizedMinutes);
        previousHoursRef.current = newHours;

        // 计算并通知外部总分钟数变化
        const totalMinutes = newHours * 60 + normalizedMinutes;
        onChange && onChange(totalMinutes);
    };

    return (
        <Space style={{ width: "100%", ...style }}>
            <InputNumber
                min={0}
                value={hours}
                onChange={handleHoursChange}
                disabled={disabled}
                addonAfter="小时"
                style={{
                    transition: "all 0.5s ease",
                    backgroundColor: hoursAnimating ? "#e6f7ff" : undefined,
                    boxShadow: hoursAnimating ? "0 0 8px rgba(31, 144, 31, 0.8)" : undefined,
                }}
                changeOnWheel={true}
                precision={0}
            />
            <InputNumber min={0} value={rawMinutes} onChange={handleMinutesChange} onFocus={handleMinutesFocus} onBlur={handleMinutesBlur} disabled={disabled} addonAfter="分钟" changeOnWheel={true} step={5} precision={0} />
        </Space>
    );
};

HourMinuteInput.propTypes = {
    value: PropTypes.number, // 总分钟数
    onChange: PropTypes.func, // 改变回调，参数为总分钟数
    disabled: PropTypes.bool, // 是否禁用
    style: PropTypes.object, // 样式
};

export default HourMinuteInput;
